import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, CheckCircle2, Grid3x3, List, Trash2, ChevronDown, Filter, Play, Plus, FileDown, FileText, Upload, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, clientsAPI } from '../../services/api';
import { Card, StatusBadge, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, formatDateTime, handleApiError, getImageUrl, showToast, showConfirm, getMediaTypeFromUrl } from '../../utils/helpers';
import { POST_STATUS, STATUS_CONFIG } from '../../utils/constants';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import PlatformPreview from './previews';
import { PlatformIcon } from '../icons/PlatformIcons';
import ImportPostsModal from './ImportPostsModal';

const PostsListPage = () => {
  const { user, isAdmin, isClient } = useAuth();
  const { isRTL, isArabic, t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');
  const [previewPost, setPreviewPost] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [approvingPostId, setApprovingPostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [postForStatusChange, setPostForStatusChange] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [statusChangeSubmitting, setStatusChangeSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterClient, filterPlatform, filterContentType]);
  
  // Reset selection when filters change
  useEffect(() => {
    setSelectedPosts(new Set());
  }, [filterStatus, filterClient, filterPlatform, filterContentType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      // For Scheduled/Published we filter على مستوى الواجهة بناءً على scheduled_at / published_at
      if (
        filterStatus !== 'all' &&
        filterStatus !== POST_STATUS.SCHEDULED &&
        filterStatus !== POST_STATUS.PUBLISHED
      ) {
        params.status = filterStatus;
      }
      if (filterClient !== 'all') {
        params.client_id = filterClient;
      }
      if (filterPlatform !== 'all') {
        params.platform = filterPlatform;
      }
      if (filterContentType !== 'all') {
        params.content_type = filterContentType;
      }

      const postsResponse = await postsAPI.getAll(params);
      let fetchedPosts = postsResponse.data.posts || [];

      // فلترة إضافية في الواجهة لحالات Scheduled / Published
      if (filterStatus === POST_STATUS.SCHEDULED) {
        fetchedPosts = fetchedPosts.filter((post) => post.scheduled_at);
      } else if (filterStatus === POST_STATUS.PUBLISHED) {
        fetchedPosts = fetchedPosts.filter((post) => post.published_at);
      }

      setPosts(fetchedPosts);

      // Fetch clients for filter (admin/agency only)
      if (!isClient) {
        const clientsResponse = await clientsAPI.getAll();
        setClients(clientsResponse.data.clients);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmed = await showConfirm(
      t('confirmApprovePost'),
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setApprovingPostId(postId);
      await postsAPI.approve(postId);
      await fetchData();
      setOpenMenuId(null);
      showToast.success(t('postApprovedSuccess'));
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setApprovingPostId(null);
    }
  };

  const handleDelete = async (postId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const confirmed = await showConfirm(
      t('confirmDeletePost'),
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeletingPostId(postId);
      await postsAPI.delete(postId);
      await fetchData();
      setOpenMenuId(null);
      showToast.success(t('postDeletedSuccess'));
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPosts.size === 0) return;

    // فقط البوستات المعلقة (pending) يمكن تعميدها
    const postsToApprove = posts.filter(
      (post) => selectedPosts.has(post.id) && post.status === POST_STATUS.PENDING
    );

    if (postsToApprove.length === 0) {
      showToast.error(
        isRTL
          ? 'لا توجد بوستات بحالة انتظار للموافقة ضمن الاختيار'
          : 'No pending posts in the current selection to approve'
      );
      return;
    }

    const confirmed = await showConfirm(
      (isRTL
        ? 'هل أنت متأكد من تعميد {count} بوست دفعة واحدة؟'
        : 'Are you sure you want to approve {count} posts at once?'
      ).replace('{count}', postsToApprove.length),
      isRTL
    );

    if (!confirmed) {
      return;
    }

    try {
      setBulkApproving(true);
      // Approve all pending posts in parallel
      const approvePromises = postsToApprove.map((post) => postsAPI.approve(post.id));
      await Promise.all(approvePromises);
      await fetchData();
      setSelectedPosts(new Set());
      showToast.success(
        (isRTL ? 'تم تعميد {count} بوست بنجاح' : '{count} posts approved successfully').replace(
          '{count}',
          postsToApprove.length
        )
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setBulkApproving(false);
    }
  };

  const openChangeStatusModal = (post, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const derived = post.scheduled_at ? POST_STATUS.SCHEDULED : (post.published_at ? POST_STATUS.PUBLISHED : post.status);
    setPostForStatusChange(post);
    setNewStatus(derived);
    setScheduledAt(post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '');
    setShowStatusModal(true);
    setOpenMenuId(null);
  };

  const handleStatusChangeSubmit = async () => {
    if (!postForStatusChange) return;
    if (newStatus === POST_STATUS.SCHEDULED && !scheduledAt) {
      showToast.error(isRTL ? 'حدد تاريخ ووقت النشر للمجدول' : 'Please set date and time for scheduled');
      return;
    }
    try {
      setStatusChangeSubmitting(true);
      const payload = newStatus === POST_STATUS.SCHEDULED
        ? { status: POST_STATUS.SCHEDULED, scheduled_at: new Date(scheduledAt).toISOString() }
        : { status: newStatus };
      await postsAPI.update(postForStatusChange.id, payload);
      await fetchData();
      setShowStatusModal(false);
      setPostForStatusChange(null);
      setNewStatus('');
      setScheduledAt('');
      showToast.success(isRTL ? 'تم تحديث حالة البوست' : 'Post status updated');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setStatusChangeSubmitting(false);
    }
  };

  // Bulk selection handlers
  const handleSelectPost = (postId) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPosts.size === posts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(posts.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPosts.size === 0) return;

    const confirmed = await showConfirm(
      t('confirmBulkDeletePosts').replace('{count}', selectedPosts.size),
      isRTL
    );

    if (!confirmed) {
      return;
    }

    try {
      setBulkDeleting(true);
      const deletePromises = Array.from(selectedPosts).map(postId => postsAPI.delete(postId));
      await Promise.all(deletePromises);
      setSelectedPosts(new Set());
      await fetchData();
      showToast.success(
        t('postsDeletedSuccess').replace('{count}', selectedPosts.size)
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk export functions
  const handleBulkExportJSON = () => {
    if (selectedPosts.size === 0) {
      showToast.error(isRTL ? 'يرجى اختيار منشورات للتصدير' : 'Please select posts to export');
      return;
    }

    try {
      const selectedPostsData = posts.filter(post => selectedPosts.has(post.id));
      const exportData = {
        exported_at: new Date().toISOString(),
        total_posts: selectedPostsData.length,
        posts: selectedPostsData.map(post => ({
          id: post.id,
          title: post.title,
          description: post.description,
          caption: post.current_caption,
          rich_description: post.rich_description,
          platform: post.platform,
          content_type: post.content_type,
          status: post.status,
          image_url: post.current_image_url,
          client_name: post.client_name,
          created_at: post.created_at,
          updated_at: post.updated_at,
        }))
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `posts-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? `تم تصدير ${selectedPosts.size} منشور` : `Exported ${selectedPosts.size} posts`);
    } catch (err) {
      console.error('Export JSON error:', err);
      showToast.error(isRTL ? 'فشل تصدير البيانات' : 'Failed to export data');
    }
  };

  const handleBulkExportCSV = () => {
    if (selectedPosts.size === 0) {
      showToast.error(isRTL ? 'يرجى اختيار منشورات للتصدير' : 'Please select posts to export');
      return;
    }

    try {
      const selectedPostsData = posts.filter(post => selectedPosts.has(post.id));
      
      // CSV headers - Serial Number is first column (only visible in Excel)
      const headers = ['Serial Number', 'ID', 'Title', 'Description', 'Caption', 'Platform', 'Content Type', 'Status', 'Image URL', 'Client Name', 'Created At', 'Updated At'];
      
      // CSV rows - Serial Number is first value
      const rows = selectedPostsData.map(post => [
        post.serial_number || '', // Serial Number (only in CSV Export)
        post.id,
        post.title || '',
        post.description || '',
        post.current_caption || '',
        post.platform || '',
        post.content_type || '',
        post.status || '',
        post.current_image_url || '',
        post.client_name || '',
        post.created_at || '',
        post.updated_at || '',
      ]);

      // Escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for UTF-8
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `posts-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? `تم تصدير ${selectedPosts.size} منشور` : `Exported ${selectedPosts.size} posts`);
    } catch (err) {
      console.error('Export CSV error:', err);
      showToast.error(isRTL ? 'فشل تصدير البيانات' : 'Failed to export data');
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${isRTL ? 'text-right' : 'text-left'}`}>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} variant="secondary">
          {t('tryAgain')}
        </Button>
      </div>
    );
  }

  const statusCounts = {
    all: posts.length,
    pending: posts.filter((p) => p.status === POST_STATUS.PENDING).length,
    needs_changes: posts.filter((p) => p.status === POST_STATUS.NEEDS_CHANGES).length,
    approved: posts.filter((p) => p.status === POST_STATUS.APPROVED).length,
    // نعتبر المنشور مجدول إذا عنده scheduled_at
    scheduled: posts.filter((p) => p.scheduled_at).length,
    // نعتبر المنشور منشور إذا عنده published_at
    published: posts.filter((p) => p.published_at).length,
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-sky-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'إدارة البوستات' : 'Posts Management'}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {t('postsTitle')}
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              {isClient ? t('postsSubtitleClient') : t('postsSubtitleAdmin')}
            </p>
          </div>

          {!isClient && (
            <div className="flex flex-wrap gap-2 items-center justify-start md:justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowImportModal(true)}
                className="border-slate-200"
              >
                <Upload className="w-4 h-4" />
                {isRTL ? 'استيراد' : 'Import'}
              </Button>
              <Link to="/posts/new">
                <Button
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  {t('createNewPost')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </Card>

      {/* Filters & View Toggle */}
      <Card className="px-4 py-2 border-slate-200 bg-white/80 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex gap-1 bg-slate-50 p-1.5 rounded-2xl border border-slate-200/60 shadow-sm">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === 'all'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {t('allPostsFilter')} ({statusCounts.all})
              </button>
              <button
                onClick={() => setFilterStatus(POST_STATUS.PENDING)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === POST_STATUS.PENDING
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {t('pendingLabel')} ({statusCounts.pending})
              </button>
              <button
                onClick={() => setFilterStatus(POST_STATUS.NEEDS_CHANGES)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === POST_STATUS.NEEDS_CHANGES
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {t('needsChangesLabel')} ({statusCounts.needs_changes})
              </button>
              <button
                onClick={() => setFilterStatus(POST_STATUS.APPROVED)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === POST_STATUS.APPROVED
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {t('approvedLabel')} ({statusCounts.approved})
              </button>
              <button
                onClick={() => setFilterStatus(POST_STATUS.SCHEDULED)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === POST_STATUS.SCHEDULED
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {isRTL ? 'مجدولة' : 'Scheduled'} ({statusCounts.scheduled})
              </button>
              <button
                onClick={() => setFilterStatus(POST_STATUS.PUBLISHED)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === POST_STATUS.PUBLISHED
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                {isRTL ? 'منشورة' : 'Published'} ({statusCounts.published})
              </button>
            </div>

            {/* Client Filter (Admin/Agency only) */}
            {!isClient && clients.length > 0 && (
              <select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm bg-white"
              >
                <option value="all">{t('filterAllClients')}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
            )}

            {/* More Filters Dropdown */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
                className={`border-slate-200 ${
                  filterPlatform !== 'all' || filterContentType !== 'all'
                    ? 'border-sky-500'
                    : ''
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>{t('moreFilters')}</span>
                {(filterPlatform !== 'all' || filterContentType !== 'all') && (
                  <span className="bg-sky-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {[filterPlatform !== 'all', filterContentType !== 'all'].filter(Boolean).length}
                  </span>
                )}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFiltersDropdown ? 'rotate-180' : ''
                  }`}
                />
              </Button>

              {showFiltersDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowFiltersDropdown(false)}
                  />
                  <div
                    className={`absolute ${
                      isRTL ? 'left-0' : 'right-0'
                    } top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 z-20 p-4 space-y-4`}
                  >
                    {/* Platform Filter */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('filterPlatformLabel')}
                      </label>
                      <select
                        value={filterPlatform}
                        onChange={(e) => setFilterPlatform(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm bg-white"
                      >
                        <option value="all">{t('filterAllPlatforms')}</option>
                        {Object.values(PLATFORMS).map((platform) => (
                          <option key={platform.id} value={platform.id}>
                            {isArabic ? platform.nameAr : platform.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Content Type Filter */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('filterContentTypeLabel')}
                      </label>
                      <select
                        value={filterContentType}
                        onChange={(e) => setFilterContentType(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm bg-white"
                      >
                        <option value="all">{t('filterAllTypes')}</option>
                        {Object.values(CONTENT_TYPES).map((type) => (
                          <option key={type.id} value={type.id}>
                            {isArabic ? type.nameAr : type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Clear Filters Button */}
                    {(filterPlatform !== 'all' || filterContentType !== 'all') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFilterPlatform('all');
                          setFilterContentType('all');
                        }}
                        className="w-full text-rose-600 hover:bg-rose-50"
                      >
                        {t('filterClear')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-full">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'grid'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title={t('gridView')}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title={t('listView')}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </Card>

      {/* Bulk Selection Toolbar (Only for List View) */}
      {isAdmin && viewMode === 'list' && selectedPosts.size > 0 && (
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-900">
              {selectedPosts.size} {selectedPosts.size === 1 ? t('postsSelected') : t('postsSelectedPlural')}
            </span>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkApprove}
                disabled={bulkApproving}
              >
                <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />
                {isRTL ? 'تعميد المحددين' : 'Approve selected'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleBulkExportJSON}
                title={isRTL ? 'تصدير JSON' : 'Export as JSON'}
              >
                <FileText className="w-4 h-4 mr-1" />
                JSON
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleBulkExportCSV}
                title={isRTL ? 'تصدير CSV' : 'Export as CSV'}
              >
                <FileDown className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                <Trash2 className="w-4 h-4" />
                {t('deleteSelected')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPosts(new Set())}
              >
                {t('clearSelection')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Select All Checkbox (Admin only - Only for List View) */}
      {isAdmin && viewMode === 'list' && posts.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={selectedPosts.size === posts.length && posts.length > 0}
            onChange={handleSelectAll}
            className="w-4 h-4 text-slate-900 rounded border-slate-300 focus:ring-slate-500"
          />
          <label className="text-sm font-medium text-slate-700">
            {t('selectAll')}
          </label>
        </div>
      )}

      {/* Posts Grid/List */}
      {posts.length === 0 ? (
        <Card className="p-12">
          <div className={`text-center ${isRTL ? 'text-right' : 'text-left'}`}>
            <p className="text-slate-500 text-lg mb-4">{t('noPostsFoundForFilters')}</p>
            {!isClient && (
              <Link to="/posts/new">
                <Button variant="primary">{t('createYourFirstPost')}</Button>
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => {
                const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
                const contentTypeInfo = post.content_type ? CONTENT_TYPES[post.content_type] : null;
                const isSelected = selectedPosts.has(post.id);
                
                return (
                  <Card key={post.id} hover className="h-full flex flex-col">
                    {/* Post Image/Video */}
                    <div className="relative">
                      {post.current_image_url ? (
                        <div className="aspect-video bg-slate-100 rounded-t-xl overflow-hidden relative group">
                          {getMediaTypeFromUrl(post.current_image_url) === 'video' ? (
                            <>
                              <video
                                src={getImageUrl(post.current_image_url)}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.className = 'aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center';
                                  e.target.parentElement.innerHTML = '<p class="text-slate-400 text-sm">No video</p>';
                                }}
                              />
                              {/* Play Icon Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-opacity">
                                <div className="bg-white bg-opacity-90 rounded-full p-3 shadow-lg">
                                  <Play className="w-10 h-10 text-slate-900" />
                                </div>
                              </div>
                            </>
                          ) : (
                            <img
                              src={getImageUrl(post.current_image_url)}
                              alt={post.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.className = 'aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center';
                                e.target.parentElement.innerHTML = '<p class="text-slate-400 text-sm">No image</p>';
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="aspect-video bg-slate-100 rounded-t-xl flex items-center justify-center">
                          <p className="text-slate-400 text-sm">{t('noImage')}</p>
                        </div>
                      )}
                      
                      {/* Platform Badge - Icon Only */}
                      {platformInfo && (
                        <div 
                          className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} flex items-center justify-center w-8 h-8 rounded-lg bg-black/50 backdrop-blur-md shadow-md`}
                          title={isArabic ? platformInfo.nameAr : platformInfo.name}
                        >
                          <PlatformIcon platformId={platformInfo.id} className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Post Info */}
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <Link 
                          to={`/posts/${post.id}`}
                          className={`font-bold text-slate-900 line-clamp-1 flex-1 hover:text-slate-700 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          {post.title}
                        </Link>
                        <StatusBadge status={post.status} size="sm" isRTL={isRTL} />
                      </div>

                      {/* Platform & Content Type Info */}
                      {(platformInfo || contentTypeInfo) && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                          {platformInfo && (
                            <span className="flex items-center gap-1">
                              <PlatformIcon platformId={platformInfo.id} className="w-4 h-4" />
                            </span>
                          )}
                          {contentTypeInfo && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <span>{contentTypeInfo.icon}</span>
                              </span>
                            </>
                          )}
                        </div>
                      )}

                      {post.description && (
                        <p className={`text-sm text-slate-600 line-clamp-2 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {post.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-xs text-slate-500 mb-2 mt-auto">
                        <span>{post.client_name}</span>
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      
                      {/* Schedule/Published Info */}
                      {(post.scheduled_at || post.published_at) && (
                        <div className="flex items-center gap-2 text-xs mt-1">
                          {post.scheduled_at && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              📅 {isRTL ? 'مجدول' : 'Scheduled'} {formatDate(post.scheduled_at)}
                            </span>
                          )}
                          {post.published_at && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                              ✓ {isRTL ? 'منشور' : 'Published'} {formatDate(post.published_at)}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-4 text-xs text-slate-400 pt-2 border-t border-slate-50">
                        {post.comment_count > 0 && (
                          <span>💬 {post.comment_count}</span>
                        )}
                        {post.version_count > 1 && (
                          <span>🔄 v{post.current_version}</span>
                        )}
                        {post.designer_name && (
                          <span className="truncate">🎨 {post.designer_name}</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-50">
                        <Link 
                          to={`/posts/${post.id}`}
                          className="flex-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="secondary" size="sm" className="w-full">
                            {t('viewDetails')}
                          </Button>
                        </Link>
                        {(post.current_image_url || (post.platform && post.content_type)) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPreviewPost(post);
                            }}
                          >
                            👁️ {t('preview')}
                          </Button>
                        )}
                        
                        {/* Actions Menu (Admin only) */}
                        {isAdmin && (
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === post.id ? null : post.id);
                              }}
                              className="p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                          
                          {openMenuId === post.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                }}
                              />
                              <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} bottom-full mb-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`}>
                                <div className="py-1">
                                  {post.status === POST_STATUS.PENDING && (
                                    <button
                                      onClick={(e) => handleApprove(post.id, e)}
                                      disabled={approvingPostId === post.id}
                                      className="w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                    >
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                      {t('approvePost')}
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => openChangeStatusModal(post, e)}
                                    className="w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 text-left"
                                    type="button"
                                  >
                                    <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
                                    {isRTL ? 'تغيير حالة البوست' : 'Change post status'}
                                  </button>
                                  <button
                                    onClick={(e) => handleDelete(post.id, e)}
                                    disabled={deletingPostId === post.id}
                                    className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {t('deletePost')}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          ) : (
            /* List View */
            <div className="space-y-3">
              {posts.map((post) => {
                const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
                const isSelected = selectedPosts.has(post.id);
                const derivedStatus = post.scheduled_at
                  ? POST_STATUS.SCHEDULED
                  : (post.published_at ? POST_STATUS.PUBLISHED : post.status);
                
                return (
                  <Link key={post.id} to={`/posts/${post.id}`} className={`block ${isSelected ? 'ring-2 ring-slate-900 border-slate-900 rounded-lg' : ''}`}>
                    <Card hover className={`p-4 ${isSelected ? 'border-slate-900' : ''}`}>
                      <div className="flex items-center gap-4">
                        {/* Bulk Selection Checkbox (Admin only) - Next to image */}
                        {isAdmin && (
                          <div className="flex-shrink-0 z-10 relative" onClick={(e) => e.preventDefault()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectPost(post.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 text-slate-900 rounded border-slate-300 focus:ring-slate-500 cursor-pointer"
                            />
                          </div>
                        )}
                        
                        {/* Image/Video - Smaller and Square */}
                        <div className="flex-shrink-0 w-24 h-24 relative">
                          {post.current_image_url ? (
                            <div className="w-full h-full bg-slate-100 rounded-lg overflow-hidden relative group">
                              {getMediaTypeFromUrl(post.current_image_url) === 'video' ? (
                                <>
                                  <video
                                    src={getImageUrl(post.current_image_url)}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                    preload="metadata"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.className = 'w-full h-full bg-slate-100 rounded-lg flex items-center justify-center';
                                      e.target.parentElement.innerHTML = '<p class="text-slate-400 text-xs">No video</p>';
                                    }}
                                  />
                                  {/* Play Icon Overlay for List View */}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-opacity">
                                    <div className="bg-white bg-opacity-90 rounded-full p-2 shadow-lg">
                                      <Play className="w-6 h-6 text-slate-900" />
                                    </div>
                                  </div>
                                  <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1 py-0.5">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </div>
                                </>
                              ) : (
                                <img
                                  src={getImageUrl(post.current_image_url)}
                                  alt={post.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.className = 'w-full h-full bg-slate-100 rounded-lg flex items-center justify-center';
                                    e.target.parentElement.innerHTML = '<p class="text-slate-400 text-xs">No image</p>';
                                  }}
                                />
                              )}
                            </div>
                          ) : (
                            <div className="w-full h-full bg-slate-100 rounded-lg flex items-center justify-center">
                              <span className="text-slate-400 text-2xl">📄</span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className={`font-semibold text-slate-900 truncate mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {post.title}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                                {platformInfo && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"
                                    title={isArabic ? platformInfo.nameAr : platformInfo.name}
                                  >
                                    <PlatformIcon platformId={platformInfo.id} className="w-3.5 h-3.5" />
                                    <span className="text-xs">
                                      {isArabic ? platformInfo.nameAr : platformInfo.name}
                                    </span>
                                  </span>
                                )}
                                <span className="truncate">{post.client_name}</span>
                                <span>•</span>
                                <span>{formatDate(post.created_at)}</span>
                              </div>
                              {post.scheduled_at && (
                                <div className="mt-1 flex items-center gap-1 text-xs text-sky-700">
                                  <Clock className="w-3 h-3" />
                                  <span>
                                    {isRTL
                                      ? `مجدول: ${formatDateTime(post.scheduled_at)}`
                                      : `Scheduled: ${formatDateTime(post.scheduled_at)}`}
                                  </span>
                                </div>
                              )}
                            </div>
                            <StatusBadge status={derivedStatus} isRTL={isRTL} />
                          </div>
                        </div>

                        {/* Actions - Minimal */}
                        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {(post.current_image_url || (post.platform && post.content_type)) && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPreviewPost(post);
                              }}
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                              title={t('preview')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Actions Menu (Admin only) */}
                          {isAdmin && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuId(openMenuId === post.id ? null : post.id);
                                }}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                              >
                                <MoreVertical className="h-5 w-5" />
                              </button>
                              
                              {openMenuId === post.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                    }}
                                  />
                                  <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-1 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`}>
                                    <div className="py-1">
                                      {post.status === POST_STATUS.PENDING && (
                                        <button
                                          onClick={(e) => handleApprove(post.id, e)}
                                          disabled={approvingPostId === post.id}
                                          className="w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                        >
                                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                                          {t('approve')}
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => openChangeStatusModal(post, e)}
                                        type="button"
                                        className="w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 text-left"
                                      >
                                        <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
                                        {isRTL ? 'تغيير حالة البوست' : 'Change post status'}
                                      </button>
                                      <button
                                        onClick={(e) => handleDelete(post.id, e)}
                                        disabled={deletingPostId === post.id}
                                        className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        {t('delete')}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Preview Modal */}
          {previewPost && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setPreviewPost(null)}
            >
              <div 
                className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">
                        {t('postPreview')}
                      </h2>
                      <p className="text-sm text-slate-600 mt-1">{previewPost.title}</p>
                    </div>
                    <button
                      onClick={() => setPreviewPost(null)}
                      className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
                    >
                      ×
                    </button>
                  </div>

                  {/* Preview Content */}
                  <div className="flex justify-center items-start gap-6">
                    <div className="flex-1">
                      <PlatformPreview
                        platform={previewPost.platform}
                        contentType={previewPost.content_type}
                        imageUrl={previewPost.current_image_url ? getImageUrl(previewPost.current_image_url) : null}
                        caption={previewPost.current_caption}
                        title={previewPost.title}
                        description={previewPost.description}
                        productDetails={previewPost.product_details}
                        articleDetails={previewPost.article_details}
                        specifications={previewPost.specifications || {}}
                        carouselItems={previewPost.current_carousel_items || []}
                        username={
                          previewPost.platform === 'instagram' && previewPost.client_social_media_accounts?.instagram
                            ? previewPost.client_social_media_accounts.instagram.replace('@', '')
                            : 'username'
                        }
                      />
                    </div>
                    
                    {/* Post Info */}
                    <div className={`w-80 space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">
                          {t('postInfo')}
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-slate-500">{t('clientLabel')}</span>
                            <span className={isRTL ? 'mr-2' : 'ml-2'}>{previewPost.client_name}</span>
                          </div>
                          {previewPost.platform && (
                            <div>
                              <span className="text-slate-500">{t('platformLabel')}</span>
                              <span className={isRTL ? 'mr-2' : 'ml-2'}>
                                {isArabic 
                                  ? PLATFORMS[previewPost.platform]?.nameAr 
                                  : PLATFORMS[previewPost.platform]?.name}
                              </span>
                            </div>
                          )}
                          {previewPost.content_type && (
                            <div>
                              <span className="text-slate-500">{t('contentTypeLabel')}</span>
                              <span className={isRTL ? 'mr-2' : 'ml-2'}>
                                {isArabic 
                                  ? CONTENT_TYPES[previewPost.content_type]?.nameAr 
                                  : CONTENT_TYPES[previewPost.content_type]?.name}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="text-slate-500">{t('dateLabel')}</span>
                            <span className={isRTL ? 'mr-2' : 'ml-2'}>{formatDate(previewPost.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t">
                        <Link to={`/posts/${previewPost.id}`}>
                          <Button variant="primary" className="w-full">
                            {t('viewFullDetails')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Change Status Modal */}
      {showStatusModal && postForStatusChange && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !statusChangeSubmitting && (setShowStatusModal(false), setPostForStatusChange(null))}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {isRTL ? 'تغيير حالة البوست' : 'Change post status'}
            </h3>
            <p className="text-sm text-slate-600 truncate mb-4" title={postForStatusChange.title}>
              {postForStatusChange.title}
            </p>
            <p className="text-xs text-slate-500 mb-3">
              {isRTL ? 'الحالة الحالية:' : 'Current:'}{' '}
              <StatusBadge status={postForStatusChange.scheduled_at ? POST_STATUS.SCHEDULED : (postForStatusChange.published_at ? POST_STATUS.PUBLISHED : postForStatusChange.status)} />
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[POST_STATUS.PENDING, POST_STATUS.NEEDS_CHANGES, POST_STATUS.APPROVED, POST_STATUS.SCHEDULED].map((status) => {
                const config = STATUS_CONFIG[status];
                const label = config?.label || status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setNewStatus(status)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      newStatus === status
                        ? config?.bgClass + ' ' + config?.textClass + ' ring-1 ring-offset-1'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {newStatus === POST_STATUS.SCHEDULED && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {isRTL ? 'تاريخ ووقت النشر' : 'Schedule date & time'}
                </label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            )}
            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="primary"
                onClick={handleStatusChangeSubmit}
                disabled={statusChangeSubmitting}
              >
                {statusChangeSubmitting ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'تطبيق' : 'Apply')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStatusModal(false);
                  setPostForStatusChange(null);
                  setNewStatus('');
                  setScheduledAt('');
                }}
                disabled={statusChangeSubmitting}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportPostsModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            fetchData();
            setSelectedPosts(new Set());
          }}
        />
      )}
    </div>
  );
};

export default PostsListPage;

